import React from 'react';
import { fmt, fmtDate } from './mpoUtils';

const LOGO = process.env.PUBLIC_URL + '/rnl-logo.png';

const sharedStyles = `
  .mpo-print-root * { box-sizing: border-box; }
  .mpo-ph { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #0b318b; padding-bottom: 10px; margin-bottom: 14px; }
  .mpo-logo { height: 44px; object-fit: contain; }
  .mpo-title { font-size: 18px; font-weight: 800; color: #0b318b; text-align: center; }
  .mpo-sub   { font-size: 11px; color: #555; text-align: center; margin-top: 2px; }
  .mpo-status { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; text-align: right; }
  .mpo-sec { font-size: 10px; font-weight: 800; color: #0b318b; text-transform: uppercase; letter-spacing: .05em; margin: 10px 0 6px; }
  .mpo-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 4px 12px; border: 1px solid #e0e0e0; border-radius: 4px; padding: 8px 10px; margin-bottom: 10px; background: #f8fafc; }
  .mpo-field { display: flex; flex-direction: column; gap: 1px; }
  .mpo-lbl { font-size: 8px; font-weight: 700; color: #666; text-transform: uppercase; letter-spacing: .05em; }
  .mpo-val { font-size: 9.5px; color: #1e293b; font-weight: 600; }
  .mpo-items table { width: 100%; border-collapse: collapse; font-size: 8px; }
  .mpo-items th { background: #0b318b; color: #fff; padding: 5px 4px; text-align: center; border: 1px solid rgba(255,255,255,0.2); font-size: 7.5px; white-space: nowrap; }
  .mpo-items td { padding: 4px 4px; border: 1px solid #ddd; text-align: center; }
  .mpo-items tr:nth-child(even) td { background: #f8fafc; }
  .mpo-items tfoot td { background: #e8eef8; font-weight: 700; }
  .mpo-items tr.mpo-rv-cc td { background: #eff6ff; color: #1e3a8a; }
  .mpo-items tr.mpo-rv-ap td { background: #dcfce7; color: #166534; }
  .mpo-items tr.mpo-rv-cc td, .mpo-items tr.mpo-rv-ap td { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .mpo-legend { font-size: 7.5px; color: #555; margin-top: 4px; }
  .mpo-sigs { display: grid; grid-template-columns: repeat(3, 1fr); gap: 0 24px; margin-top: 20px; }
  .mpo-sig { border-top: 1px solid #444; padding-top: 4px; text-align: center; font-size: 8px; color: #555; }
  .mpo-sig-space { height: 30px; }
`;

const num = (v) => (v != null && v !== '' ? fmt(v) : '—');
const qtyText = (v) => (v != null && v !== '' ? Number(v).toLocaleString('en-NG', { maximumFractionDigits: 4 }) : '—');
const hasCC = (it) => ['cost_control_quantity', 'cost_control_unit_rate', 'cost_control_amount'].some((k) => it[k] != null)
  || it.cost_control_approved || it.cost_control_rejected;
const hasAppr = (it) => ['approved_quantity', 'approved_unit_rate', 'approved_amount'].some((k) => it[k] != null)
  || it.approved || it.on_hold || it.rejected;

// The reviews a line went through, under it: Cost Control (blue) and Management's approved figures (green) —
// the same rows the downloaded PDF prints. Label across cols 2-10, qty / rate / amount in their columns.
function ReviewRow({ kind, label, detail, qty, rate, amount, decision }) {
  const mono = { fontFamily: 'monospace', fontWeight: 700 };
  return (
    <tr className={kind === 'cc' ? 'mpo-rv-cc' : 'mpo-rv-ap'}>
      <td />
      <td colSpan={9} style={{ textAlign: 'left' }}><b>{label}</b>{detail ? ` — ${detail}` : ''}</td>
      <td style={mono}>{qtyText(qty)}</td>
      <td />
      <td style={mono}>{num(rate)}</td>
      <td /><td />
      <td style={mono}>{num(amount)}</td>
      <td />
      <td style={{ fontWeight: 700 }}>{decision}</td>
    </tr>
  );
}

function Field({ label, value }) {
  return (
    <div className="mpo-field">
      <span className="mpo-lbl">{label}</span>
      <span className="mpo-val">{value ?? '—'}</span>
    </div>
  );
}

export default function MPOPrintView({ po }) {
  if (!po) return null;
  const items = po.items || [];
  const ccLines = items.filter((it) => it.cost_control_amount != null && !it.cost_control_rejected);
  const apLines = items.filter((it) => it.approved && it.approved_amount != null);
  const sum = (list, key) => list.reduce((t, it) => t + Number(it[key] || 0), 0);

  const printStyles = `
    .mpo-print-root { display: none; }
    @media print {
      body * { visibility: hidden !important; }
      .mpo-print-root, .mpo-print-root * { visibility: visible !important; }
      .mpo-print-root {
        display: block !important;
        position: absolute; left: 0; top: 0; width: 100%;
        padding: 0; margin: 0; background: #fff; color: #000;
        font-family: Arial, Helvetica, sans-serif;
      }
      @page { size: A4 landscape; margin: 12mm; }
      ${sharedStyles}
      .mpo-wm {
        position: fixed; top: 50%; left: 50%;
        transform: translate(-50%, -50%) rotate(35deg);
        font-size: 72px; font-weight: 900; color: #0b318b;
        opacity: 0.05; z-index: 0; pointer-events: none; white-space: nowrap;
      }
    }
  `;

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: printStyles }} />
      <div className="mpo-print-root">
        <div className="mpo-wm">PURCHASE ORDER</div>

        {/* Page header */}
        <div className="mpo-ph">
          <img src={LOGO} alt="RNL" className="mpo-logo" />
          <div style={{ textAlign: 'center' }}>
            <div className="mpo-title">MATERIAL PURCHASE ORDER</div>
            <div className="mpo-sub">PO No: {po.po_number || '—'} · Serial No: {po.serial_number}</div>
          </div>
          <div className="mpo-status" style={{ color: '#0b318b' }}>
            Status: {(po.status || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
          </div>
        </div>

        {/* PO header fields */}
        <div className="mpo-sec">Section 1 — Purchase Order Header</div>
        <div className="mpo-grid">
          <Field label="PO Number"          value={<span style={{ fontFamily: 'monospace', color: '#0b318b' }}>{po.po_number || '—'}</span>} />
          <Field label="Serial No."         value={<span style={{ fontFamily: 'monospace', color: '#475569' }}>{po.serial_number}</span>} />
          <Field label="Project"            value={po.project_name} />
          <Field label="Supplier"           value={po.supplier_name} />
          <Field label="Currency"           value={po.currency || 'NGN'} />
          <Field label="PO Date"            value={fmtDate(po.po_date)} />
          <Field label="Expected Delivery"  value={fmtDate(po.expected_delivery_date)} />
          <Field label="Payment Terms"      value={po.payment_terms} />
          <Field label="Delivery Terms"     value={po.delivery_terms} />
          <Field label="Submitted By"       value={po.submitted_by_name} />
          <Field label="Submitted Date"     value={fmtDate(po.submitted_date)} />
          <Field label="Grand Total"        value={<span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#0b318b' }}>{fmt(po.grand_total)}</span>} />
          {po.notes && <Field label="Notes" value={po.notes} />}
        </div>

        {/* Line items */}
        {items.length > 0 && (
          <>
            <div className="mpo-sec">Section 2 — Line Items</div>
            <div className="mpo-items">
              <table>
                <thead>
                  <tr>
                    {['#','Item Code','Description','New Item Desc','Spec','Warehouse','Req Ref','BOQ Code',
                      'Rem Rqst Qty','Unit','Qty','Last Pur Pr','Price (₦)','Disc%','VAT%','Total (₦)','VAT Amt (₦)','Remark']
                      .map(h => <th key={h}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, i) => (
                    <React.Fragment key={item.item_id || i}>
                    <tr>
                      <td>{item.line_number || i + 1}</td>
                      <td style={{ textAlign: 'left' }}>{item.material_code || '—'}</td>
                      <td style={{ textAlign: 'left' }}>{item.material_description || '—'}</td>
                      <td style={{ textAlign: 'left' }}>{item.new_item_description || '—'}</td>
                      <td style={{ textAlign: 'left' }}>{item.specification || '—'}</td>
                      <td>{item.warehouse || '—'}</td>
                      <td>{item.request_ref || '—'}</td>
                      <td>{item.boq_item_code || '—'}</td>
                      <td style={{ fontFamily: 'monospace' }}>{item.rem_rqst_qty != null ? fmt(item.rem_rqst_qty) : '—'}</td>
                      <td>{item.unit_of_measure || '—'}</td>
                      <td style={{ fontFamily: 'monospace' }}>{fmt(item.quantity_ordered)}</td>
                      <td style={{ fontFamily: 'monospace' }}>{item.last_pur_pr != null ? fmt(item.last_pur_pr) : '—'}</td>
                      <td style={{ fontFamily: 'monospace' }}>{fmt(item.unit_rate)}</td>
                      <td>{item.disc != null ? `${item.disc}%` : '—'}</td>
                      <td>{item.vat_percentage != null ? `${item.vat_percentage}%` : '—'}</td>
                      <td style={{ fontFamily: 'monospace', fontWeight: 700, color: '#0b318b' }}>{fmt(item.amount)}</td>
                      <td style={{ fontFamily: 'monospace' }}>{item.vat_amount != null ? fmt(item.vat_amount) : '—'}</td>
                      <td style={{ textAlign: 'left' }}>{item.remarks || '—'}</td>
                    </tr>
                    {hasCC(item) && (
                      <ReviewRow
                        kind="cc" label="COST CONTROL"
                        detail={[
                          (item.cost_control_general_ledger || po.cost_control_general_ledger) && `GL ${item.cost_control_general_ledger || po.cost_control_general_ledger}`,
                          item.boq_item_description && `BOQ ${item.boq_item_description}`,
                          item.cost_control_rejected && item.cost_control_rejected_reason && `Reason: ${item.cost_control_rejected_reason}`,
                        ].filter(Boolean).join(' · ')}
                        qty={item.cost_control_quantity} rate={item.cost_control_unit_rate} amount={item.cost_control_amount}
                        decision={item.cost_control_approved ? 'Accepted' : item.cost_control_rejected ? 'Rejected' : '—'}
                      />
                    )}
                    {hasAppr(item) && (
                      <ReviewRow
                        kind="ap" label="MANAGEMENT APPROVED"
                        detail={(item.rejected || item.on_hold) && item.rejected_reason ? `Reason: ${item.rejected_reason}` : ''}
                        qty={item.approved_quantity} rate={item.approved_unit_rate} amount={item.approved_amount}
                        decision={item.approved ? 'Approved' : item.rejected ? 'Rejected' : item.on_hold ? 'On Hold' : '—'}
                      />
                    )}
                    </React.Fragment>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={15} style={{ textAlign: 'right', fontWeight: 700 }}>Subtotal</td>
                    <td style={{ fontFamily: 'monospace' }}>{fmt(po.subtotal_amount)}</td>
                    <td style={{ fontFamily: 'monospace' }}>{fmt(po.vat_amount)}</td>
                    <td />
                  </tr>
                  <tr>
                    <td colSpan={14} style={{ textAlign: 'right', fontWeight: 700 }}>Grand Total</td>
                    <td colSpan={3} style={{ fontFamily: 'monospace', fontWeight: 800, color: '#0b318b', fontSize: 10 }}>
                      {fmt(po.grand_total)}
                    </td>
                    <td />
                  </tr>
                  {ccLines.length > 0 && (
                    <tr className="mpo-rv-cc">
                      <td colSpan={15} style={{ textAlign: 'right', fontWeight: 700 }}>Cost Control Total</td>
                      <td style={{ fontFamily: 'monospace', fontWeight: 800 }}>{fmt(sum(ccLines, 'cost_control_amount'))}</td>
                      <td /><td />
                    </tr>
                  )}
                  {apLines.length > 0 && (
                    <tr className="mpo-rv-ap">
                      <td colSpan={15} style={{ textAlign: 'right', fontWeight: 700 }}>Management Approved Total</td>
                      <td style={{ fontFamily: 'monospace', fontWeight: 800 }}>{fmt(sum(apLines, 'approved_amount'))}</td>
                      <td /><td />
                    </tr>
                  )}
                </tfoot>
              </table>
              {items.some((it) => hasCC(it) || hasAppr(it)) && (
                <div className="mpo-legend">
                  <b style={{ color: '#1e3a8a' }}>Blue rows</b>: Cost Control review. <b style={{ color: '#166534' }}>Green rows</b>: Management approved figures.
                </div>
              )}
            </div>
          </>
        )}

        {/* Signatures */}
        <div className="mpo-sigs">
          {[
            { label: 'Submitted By', name: po.submitted_by_name || '' },
            { label: 'Approved By',  name: '' },
            { label: 'Received By',  name: '' },
          ].map(({ label, name }) => (
            <div key={label}>
              <div className="mpo-sig-space" />
              <div className="mpo-sig">
                <div>{label}</div>
                {name && <div style={{ fontWeight: 700, color: '#1e293b' }}>{name}</div>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
